import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { useCustomers } from "@/hooks/use-customers";
import { useCustomerStatuses } from "@/hooks/use-customer-status";
import { useAllCustomerOwners } from "@/hooks/use-customer-owners";
import { useOffers } from "@/hooks/use-offers-queries";
import { FALLBACK_WEIGHTS, toPipelineOffer, useOfferPipelineWeights } from "@/hooks/use-offer-pipeline";
import { useOpportunities } from "@/hooks/use-opportunities";
import { useUsers } from "@/hooks/use-users";
import {
  buildCustomerListRows,
  type CustomerListRow,
  type ListCompanyInfo,
  type ListFollowUp,
  type ListLastContactRow,
  type ListOffer,
  type ListOfferLine,
  type ListProject,
} from "@/lib/customer-list-calc";

/**
 * Kundeliste v2 (VIB-2891): alle tallene per kunde i noen få team-brede
 * spørringer, aldri én per rad. Beregningen bor i customer-list-calc.ts og
 * gjenbruker kundekort v3 (GEEK i pipe fra tilbudslinjene, GEEK levert fra
 * bf_actual). Alle ubundne lesinger går via fetchAllPaginated; linjene er
 * bundet på de sendte tilbudenes id-er.
 */

const LINE_COLUMNS = "offer_id, phase, cost_type, pricing_type, quantity, unit, unit_price, purchase_price, amount, overtime_split";
const PROJECT_COLUMNS = "id, customer_id, total_amount, bf_estimated, bf_actual, created_at, is_internal, project_status, completed_at";
const FOLLOW_UP_COLUMNS = "id, customer_id, title, due_date, channel, source, rule_key";
const COMPANY_INFO_COLUMNS = "customer_id, industry_description, municipality";

const STALE = 60 * 1000;

export interface CustomerListData {
  rows: CustomerListRow[];
  isLoading: boolean;
  error: Error | null;
}

export function useCustomerList(options: { enabled?: boolean } = {}): CustomerListData {
  const enabled = options.enabled ?? true;
  const customers = useCustomers();
  const statuses = useCustomerStatuses({ enabled });
  const owners = useAllCustomerOwners({ enabled });
  const offers = useOffers();
  const { data: weights = FALLBACK_WEIGHTS } = useOfferPipelineWeights();
  const opportunities = useOpportunities({ enabled });
  const users = useUsers();

  const sentOfferIds = useMemo(
    () => (offers.data ?? []).filter((o) => o.status === "sent").map((o) => o.id).sort(),
    [offers.data],
  );

  const lines = useQuery({
    queryKey: ["customer-list", "sent-offer-lines", sentOfferIds],
    queryFn: () =>
      fetchAllPaginated<ListOfferLine>("offer_lines", LINE_COLUMNS, (q) => q.in("offer_id", sentOfferIds)),
    enabled: enabled && sentOfferIds.length > 0,
    staleTime: STALE,
  });
  const projects = useQuery({
    queryKey: ["customer-list", "projects"],
    queryFn: () => fetchAllPaginated<ListProject>("projects", PROJECT_COLUMNS, (q) => q.is("deleted_at", null)),
    enabled,
    staleTime: STALE,
  });
  const followUps = useQuery({
    queryKey: ["customer-list", "open-follow-ups"],
    queryFn: () =>
      fetchAllPaginated<ListFollowUp>("follow_ups", FOLLOW_UP_COLUMNS, (q) => q.eq("status", "open"), ["due_date", "id"]),
    enabled,
    staleTime: STALE,
  });
  const lastContacts = useQuery({
    queryKey: ["customer-list", "last-contact"],
    queryFn: () => fetchAllPaginated<ListLastContactRow>("customer_last_contact_v", "*", undefined, "customer_id"),
    enabled,
    staleTime: STALE,
  });
  const companyInfo = useQuery({
    queryKey: ["customer-list", "company-info"],
    queryFn: () => fetchAllPaginated<ListCompanyInfo>("customer_company_info", COMPANY_INFO_COLUMNS, undefined, "customer_id"),
    enabled,
    staleTime: STALE,
  });

  const queries = [customers, statuses, owners, offers, opportunities, users, lines, projects, followUps, lastContacts, companyInfo];
  const isLoading = queries.some((q) => q.isLoading);
  const error = (queries.find((q) => q.error)?.error as Error | undefined) ?? null;

  const rows = useMemo(() => {
    if (isLoading || error) return [];
    const listOffers: ListOffer[] = (offers.data ?? [])
      .filter((o) => o.status === "sent")
      .map((o) => ({
        ...toPipelineOffer(o),
        customer_id: o.customer_id,
        overhead_pct: o.overhead_pct,
        discount_pct: o.discount_pct,
        discount_amount: o.discount_amount,
        include_travel_in_overhead: o.include_travel_in_overhead,
      }));
    return buildCustomerListRows({
      customers: customers.data ?? [],
      statuses: statuses.data ?? [],
      owners: owners.data ?? [],
      projects: projects.data ?? [],
      offers: listOffers,
      offerLines: lines.data ?? [],
      weights,
      openFollowUps: followUps.data ?? [],
      opportunities: opportunities.data ?? [],
      lastContacts: lastContacts.data ?? [],
      companyInfo: companyInfo.data ?? [],
      userNames: new Map((users.data ?? []).map((u) => [u.id, u.full_name ?? ""])),
      today: new Date(),
    });
  }, [
    isLoading, error, customers.data, statuses.data, owners.data, offers.data, projects.data, lines.data,
    weights, followUps.data, opportunities.data, lastContacts.data, companyInfo.data, users.data,
  ]);

  return { rows, isLoading, error };
}
